import { useMemo } from 'react'

import { CreatorPermission, UserSettingsUiTextRatioCalculation } from '@api'
import { useUnit } from 'effector-react'
import type { DateRange } from 'react-day-picker'
import { useQueries } from 'react-query'

import { $isCreatorProcessCompleted } from '@/models/creators.ts'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { CreatorFeatures } from '@/modules/accounts/types'
import type { Creator } from '@/modules/creators/types'
import { isActiveCreator } from '@/modules/creators/utils.ts'
import type { DetailedComparisonType } from '@/modules/overview/types.ts'
import { statisticsService } from '@/modules/statistics/onlyfans/service/statistics-service.ts'
import { hasPermission } from '@/utils/helpers'

import { mapDetailedComparisonData } from '../utils'

export const useCreatorOverviewComparison = (
  dateRange: DateRange,
  selectedCreators: Creator[],
) => {
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)
  const { userSettings } = useAccount()
  const comparisonQueries = useQueries(
    selectedCreators.map((creator) => {
      return {
        queryKey: ['creatorStatistic', creator.id, dateRange],
        queryFn: () => {
          if (
            !dateRange ||
            !hasPermission(
              creator,
              CreatorFeatures.stats,
              CreatorPermission.SEE_STATS,
            ) ||
            !isActiveCreator(creator)
          ) {
            return
          }

          return statisticsService
            .getCreatorStats(
              [creator.id],
              dateRange.from || new Date(),
              dateRange.to || new Date(),
            )
            .then((data) =>
              mapDetailedComparisonData(
                data,
                creator,
                userSettings?.textRatioCalculation ===
                  UserSettingsUiTextRatioCalculation.USE_ALL_SUBS_REVENUE,
              ),
            )
        },
      }
    }),
  )

  return useMemo(
    () => ({
      data: comparisonQueries
        .map((res) => res.data)
        .filter((item) => !!item)
        .map((res) => res as DetailedComparisonType)
        .map((detailed) => {
          return {
            ...detailed,
            creatorProfilePicture: selectedCreators?.find(
              (creator) => creator.id === detailed.creatorId,
            )?.avatar.croppedUrl,
          }
        })
        .sort((a, b) => b.totalRevenue - a.totalRevenue),
      isFetching:
        !isCreatorProcessCompleted ||
        comparisonQueries.some((result) => result.isFetching || result.isIdle),
      refetch: () => comparisonQueries.forEach((query) => query.refetch()),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comparisonQueries],
  )
}
